import dayjs from 'dayjs';
import { IdSpan } from 'components/AgGrid/CellRenderer/ListView.styled';
import { StatusDot } from 'components/StatusIndicator/IgnitionStatusIndicator';

import {
  AssetId,
  type AssetListRendererProp,
  type AlertListRendererProp,
  DeviceStatus,
  AssetType,
  OperationalStatus,
  AlertStatus,
  DeviceIngestDate,
} from 'views/Settings/ExternalUsers/AssetView/components/AssetListCellRenderer';
import { truncateTo2dp } from 'utils/helpers/general';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { resolveAssetTypeDisplay } from 'utils/helpers/assets';
import { handleAssetStatus } from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { PNG } from 'components/Asset/PNG';
import { getImageName, mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { isAssetIgnition } from 'views/FleetOverview/helpers/asset.helpers';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import TerrainFourWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourWheels';
import TerrainSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/SixWheels';
import TerrainFourSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourSixWheels';
import ThreeAxlesSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesSixWheels';
import ThreeAxlesTenWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesTenWheels';
import {
  getDynamicAssetTireInfo,
  getOrderedTireCodes,
  getTireLimits,
  getTireValuesFromSensorData,
  getLastUpdatedTimestamp,
  getTirePositionKey,
} from 'views/TirePressure/util';
import { determineTireStatus, TireStatus } from 'utils/tpms';
import { convertKpaToPsi, convertCentToFahr } from 'utils/helpers/unitSystem';
import { getLocationColor } from 'utils/helpers/locationColors';

export const AssetIdForTable = ({
  handleIdClick,
  data,
}: AssetListRendererProp) => {
  return AssetId({
    handleIdClick,
    data,
  });
};

export const DeviceStatusForTable = ({
  data,
  handleDeviceClick,
}: AssetListRendererProp) => {
  return DeviceStatus({
    data,
    handleDeviceClick,
    // do nothing
    handleNoDeviceClick: () => {},
  });
};

export const DeviceIngestDateForTable = ({ data }: AssetListRendererProp) => {
  return DeviceIngestDate({
    data,
  });
};

export const LastLocationForTable = ({ data }: AssetListRendererProp) => {
  const location = data.device?.last_location;
  if (!location) return '-';

  return (
    <span style={{ color: getLocationColor(location), fontWeight: 700 }}>
      {location}
    </span>
  );
};

export const AlertStatusForTable = ({
  data,
  handleAlertClick,
}: AlertListRendererProp) => {
  return (
    <IdSpan
      onClick={() => {
        handleAlertClick(data.notifications, data.bumperNumber);
      }}
    >
      {AlertStatus({ data })}
    </IdSpan>
  );
};

export const AssetTypeForTable = ({
  data,
  handleIdClick,
}: AssetListRendererProp) => {
  return (
    <IdSpan
      onClick={() => {
        handleIdClick(
          data.assetId,
          data.assetVin,
          data.make,
          data.model,
          data.bumperNumber
        );
      }}
    >
      {AssetType({ data })}
    </IdSpan>
  );
};

export const BumperNumberForTable = ({
  data,
  handleIdClick,
  latestTagData,
}: AssetListRendererProp) => {
  const ignitionStatus = isAssetIgnition(latestTagData, data);
  return (
    <>
      <StatusDot isActive={ignitionStatus} />

      <IdSpan
        onClick={() => {
          handleIdClick(
            data.assetId,
            data.assetVin,
            data.make,
            data.model,
            data.bumperNumber
          );
        }}
      >
        {data?.bumperNumber}
      </IdSpan>
    </>
  );
};

export const OperationalStatusForTable = ({ data }: AssetListRendererProp) => {
  return OperationalStatus({ data });
};

export const TripsCompletedForTable = ({
  data,
  latestOperationalData,
}: any) => {
  const deviceId = data.device?.deviceId;
  const trips = latestOperationalData?.[deviceId]?.tripCount;
  return trips === 0 ? '-' : trips ?? '-';
};

export const UtilizationForTable = ({ data, latestOperationalData }: any) => {
  const deviceId = data.device?.deviceId;
  const utilization = latestOperationalData?.[deviceId]?.utilization;
  if (utilization === 0) return '-';
  return utilization !== undefined ? `${utilization}%` : '-';
};

export const ProductionForTable = ({ data, latestOperationalData }: any) => {
  const deviceId = data.device?.deviceId;
  const tonnage = latestOperationalData?.[deviceId]?.tonnage;
  if (tonnage === 0) return '-';
  return tonnage !== undefined ? truncateTo2dp(tonnage) : '-';
};

export const EngineStartTimeForTable = ({
  data,
  latestOperationalData,
}: any) => {
  const deviceId = data.device?.deviceId;
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const startTime = latestOperationalData?.[deviceId]?.engineStartTime;
  const shift = latestOperationalData?.[deviceId]?.shift;
  if (!startTime) return '-';

  // Raw Athena TIMESTAMP: "2026-02-19 06:09:58.000" (UTC)
  // Strip milliseconds before passing to converter
  const cleanTime = startTime.split('.')[0]; // "2026-02-19 06:09:58"
  const displayTime = convertUtcToDisplayTime(
    cleanTime,
    preferredTimeDisplayMode,
    timezone,
    'MMMM DD, hh:mm A'
  );

  const formattedShift = shift
    ? ` (${
        shift === 'shift_1'
          ? 'Day'
          : shift === 'shift_2'
          ? 'Night'
          : shift
              .replace('_', ' ')
              .replace(/\b\w/g, (l: string) => l.toUpperCase())
      })`
    : '';

  return (
    <span>
      {displayTime}
      {formattedShift}
    </span>
  );
};

export const EngineTemperatureForTable = ({ data }: any) => {
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const tagValue = data?.liveTagData?.engine_coolant_temperature_value;

  if (tagValue === undefined || tagValue === null) return '--';

  const numericValue = parseFloat(tagValue);
  if (isNaN(numericValue)) return '--';

  const displayValue =
    unitSystem === 'imperial'
      ? Math.round(convertCentToFahr(numericValue))
      : Math.round(numericValue);

  return <span>{displayValue}</span>;
};

export const AssetInformationRenderer = ({
  data,
  handleIdClick,
}: AssetListRendererProp) => {
  const { enableDarkTheme } = useAppTheme();

  const png = getImageName(data, 'Small');
  const assetTypeLabel = resolveAssetTypeDisplay(data.assetType as any);

  const operationalStatus = handleAssetStatus(data.isActive);
  const isOperational = operationalStatus === 'operational' || !!data.isActive;
  const operationalLabel = isOperational ? 'Operational' : 'Out of Service';
  const operationalColor = isOperational ? '#749A4A' : '#A03434';

  // Dot = ignition on/off like other pages (AssetMap, IgnitionStatusIndicator)
  const latestTagDataForAsset = { [data.device?.deviceId]: data.liveTagData };
  const isIgnitionOn = isAssetIgnition(latestTagDataForAsset, data);
  const dotColor = isIgnitionOn ? '#199161' : '#fc0000';
  const dotTooltip = isIgnitionOn ? 'Vehicle is ON' : 'Vehicle is OFF';

  const primaryTextColor = enableDarkTheme ? '#F8FAFC' : '#0F172A';
  const mutedTextColor = enableDarkTheme ? '#94A3B8' : '#64748B';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        gap: '12px',
      }}
    >
      <PNG
        name={png}
        style={{
          height: '42px',
          width: '64px',
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '0',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            fontSize: '13px',
            color: primaryTextColor,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            lineHeight: '1.2',
          }}
        >
          <span
            title={dotTooltip}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: dotColor,
              display: 'inline-block',
              flexShrink: 0,
              cursor: 'default',
            }}
          />
          {data.bumperNumber}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: operationalColor,
            fontWeight: 600,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            lineHeight: '1.2',
          }}
        >
          {operationalLabel}
        </span>
      </div>
    </div>
  );
};

export const TirePressureForTable = ({ data }: AssetListRendererProp) => {
  const { limits, batteryData } = useAppSelector(
    (state) => state.tireProfileReducer
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;

  if (!data?.tpmsEnabled) return '-';

  if (!data.device?.deviceId) return '-';

  const liveTagData = data.liveTagData || {};

  // Replicate logic from FleetInsight.tsx but for single asset
  const metricType = 'pressure'; // As requested "same way for pressure"

  // Extract TPMS data from liveTagData
  // Tags follow format: tpms_tire_0x17_pressure_value, tpms_tire_0x17_pressure_ts, etc.
  const tpmsData: any = {};
  const dataKeys: string[] = [];

  Object.entries(liveTagData).forEach(([key, value]) => {
    // Matches tpms_tire_0x17_pressure or tpms_tire_0x17_pressure_value
    const match = key.match(/^tpms_tire_(0x[0-9a-fA-F]+)_pressure(_value)?$/);
    if (match) {
      const tireCode = match[1];
      if (!dataKeys.includes(tireCode)) {
        dataKeys.push(tireCode);
      }

      // Timestamp key is usually tpms_tire_0x17_pressure_ts
      const tsKey = `tpms_tire_${tireCode}_pressure_ts`;
      tpmsData[tireCode] = {
        value: value,
        ts: Number(liveTagData[tsKey]),
        device_sensor: tireCode,
      };
    }
  });

  if (dataKeys.length === 0) return '-';

  const assetTireDetails = getDynamicAssetTireInfo(
    makeModelDetails,
    tirePatterns.tirePatternPositions,
    data.make,
    data.model,
    customerId
  );

  const itemPattern = data.patternName ?? assetTireDetails.patternName;
  const { tireCodes } = assetTireDetails;
  const currentLimit = getTireLimits(data.make, data.model, limits);
  const orderedKeys = getOrderedTireCodes(dataKeys, itemPattern);

  // Transform data for schematics
  const dataValues = Object.values(tpmsData);
  const values = getTireValuesFromSensorData(
    orderedKeys,
    dataValues,
    currentLimit,
    unitSystem,
    data.make,
    data.model,
    metricType
  );

  const lastUpdated = getLastUpdatedTimestamp(dataValues);
  const isDataStale = (() => {
    if (lastUpdated === 'N/A') return false;
    const lastUpdateTime = new Date(lastUpdated);
    const now = new Date();
    const hoursDiff =
      (now.getTime() - lastUpdateTime.getTime()) / (1000 * 60 * 60);
    return hoursDiff > 24;
  })();

  const tireStatusMap: Record<string, TireStatus> = {};
  const tireStaleMap: Record<string, boolean> = {};
  const tireTooltipData: any = {};

  orderedKeys.forEach((key, index) => {
    const tireNumber = index + 1;
    const pKey = `p${tireNumber}`;
    const itemData = tpmsData[key];

    // Status calculation
    let status: TireStatus = TireStatus.NO_DATA;
    if (itemData?.ts && itemData?.value !== null) {
      const tireLimit = currentLimit.find((l: any) => l.tireCode === key) || {};
      const maxValue =
        unitSystem === 'metric'
          ? tireLimit.maxPressure
          : convertKpaToPsi(tireLimit.maxPressure);
      const minValue =
        unitSystem === 'metric'
          ? tireLimit.minPressure
          : convertKpaToPsi(tireLimit.minPressure);

      status = determineTireStatus(
        Number(itemData.value),
        { min: minValue, max: maxValue },
        true,
        metricType
      );
    }
    tireStatusMap[pKey] = status;

    // Staleness
    const tireTs = itemData?.ts;
    const hoursDiff = tireTs ? (Date.now() - tireTs) / (1000 * 60 * 60) : 0;
    tireStaleMap[pKey] = hoursDiff > 24;

    // Tooltip data
    const unit = unitSystem === 'metric' ? 'KPA' : 'PSI';
    const posKey = getTirePositionKey(key, itemPattern, dataKeys);
    if (posKey) {
      tireTooltipData[posKey] = {
        name: tireCodes[key] || key,
        displayValue:
          itemData?.value !== undefined ? truncateTo2dp(itemData.value) : 'N/A',
        unit,
      };
    }

    values[`${pKey}HasSensor`] = true;
    values[`${pKey}IsStale`] = tireStaleMap[pKey];
  });
  values.isDataStale = isDataStale;

  const deviceBattery = batteryData?.[data.device.deviceId];
  let batteryLowMap: any = {};
  if (itemPattern === '10x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x26']?.isBatteryLow,
      p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
      p7: !!deviceBattery?.['0x37']?.isBatteryLow,
      p8: !!deviceBattery?.['0x39']?.isBatteryLow,
      p9: !!deviceBattery?.['0x36']?.isBatteryLow,
      p10: !!deviceBattery?.['0x3A']?.isBatteryLow,
    };
  } else if (itemPattern === '6x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x37']?.isBatteryLow,
      p6: !!deviceBattery?.['0x39']?.isBatteryLow,
    };
  } else if (itemPattern === '6x4') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x26']?.isBatteryLow,
      p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
    };
  } else if (itemPattern === '4x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x16']?.isBatteryLow,
      p2: !!deviceBattery?.['0x17']?.isBatteryLow,
      p3: !!deviceBattery?.['0x19']?.isBatteryLow,
      p4: !!deviceBattery?.['0x1A']?.isBatteryLow,
      p5: !!deviceBattery?.['0x27']?.isBatteryLow,
      p6: !!deviceBattery?.['0x29']?.isBatteryLow,
    };
  } else {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
    };
  }

  const schematicWidth = 70;
  const schematicHeight =
    itemPattern === '10x6'
      ? schematicWidth * 2.0
      : schematicWidth *
        (itemPattern.startsWith('6') || itemPattern === '4x6' ? 0.8 : 0.6);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        transform: 'rotate(90deg)',
      }}
    >
      {itemPattern === '6x4' ? (
        <TerrainSixWheels
          values={values}
          tireData={tireTooltipData}
          batteryLow={batteryLowMap}
          tireStatusMap={tireStatusMap}
          tireStaleMap={tireStaleMap}
          height={schematicHeight}
          width={schematicWidth}
        />
      ) : itemPattern === '4x6' ? (
        <TerrainFourSixWheels
          values={values}
          tireData={tireTooltipData}
          batteryLow={batteryLowMap}
          tireStatusMap={tireStatusMap}
          tireStaleMap={tireStaleMap}
          height={schematicHeight}
          width={schematicWidth}
        />
      ) : itemPattern === '6x6' ? (
        <ThreeAxlesSixWheels
          values={values}
          tireData={tireTooltipData}
          batteryLow={batteryLowMap}
          tireStatusMap={tireStatusMap}
          tireStaleMap={tireStaleMap}
          height={schematicHeight}
          width={schematicWidth}
        />
      ) : itemPattern === '10x6' ? (
        <ThreeAxlesTenWheels
          values={values}
          tireData={tireTooltipData}
          batteryLow={batteryLowMap}
          tireStatusMap={tireStatusMap}
          tireStaleMap={tireStaleMap}
          height={schematicHeight}
          width={schematicWidth}
        />
      ) : (
        <TerrainFourWheels
          values={values}
          tireData={tireTooltipData}
          batteryLow={batteryLowMap}
          tireStatusMap={tireStatusMap}
          tireStaleMap={tireStaleMap}
          height={schematicHeight}
          width={schematicWidth}
        />
      )}
    </div>
  );
};

export const AssetLiveTagDataColumnRenderer = ({
  data,
  colDef,
}: AssetListRendererProp) => {
  const tagName = colDef.field;
  const tagValue = data.liveTagData?.[tagName];
  const currentOdometerOffset = data.odometerOffset;

  let response: number | string = '--';

  if (tagValue) {
    if (tagName?.includes('gps')) {
      response = tagValue;
    } else if (tagName?.includes('edge_odometer')) {
      response = (tagValue as number) / 1000 + +currentOdometerOffset;
    } else {
      response = formatNumberOrString(tagValue) ?? '--';
    }
  }
  return response;
};

function formatNumberOrString(
  input: string | number | undefined
): string | number | undefined {
  if (input === undefined) {
    return undefined;
  }

  let parsedValue: number;

  if (typeof input === 'number') {
    // If the input is already a number, return it
    return input;
  } else if (typeof input === 'string') {
    parsedValue = parseFloat(input);

    if (isNaN(parsedValue)) {
      // If parsing as a number fails, return the original input
      return input;
    }
  } else {
    // If the input is neither a string nor a number, return the original input
    return input;
  }

  // Check if the parsedValue has more than 2 decimal places
  const decimalPlaces = (parsedValue.toString().split('.')[1] || []).length;

  // If the number has more than 2 decimal places, truncate to 2 decimal places
  const formattedValue =
    decimalPlaces > 2 ? truncateTo2dp(parsedValue) : parsedValue;

  return formattedValue;
}

export function formatDataToSort(data: any, tagValue: any, tagName: string) {
  const currentOdometerOffset: number = data.odometerOffset;

  let response: number | string = '--';

  if (tagValue) {
    if (tagName?.includes('gps')) {
      response = tagValue;
    } else if (tagName?.includes('edge_odometer')) {
      response = (tagValue as number) / 1000 + currentOdometerOffset;
    } else {
      response = formatNumberOrString(tagValue) ?? '--';
    }
  }
  return response;
}
